import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setActionBoxCategoryInfo } from '../slices/actionBoxCategorySlice'
import { generateRESTQuery, getBaseQuery } from './apiUtils'
import apiConstants, { HTTP_METHODS } from './constants'

export const actionBoxCategoriesApi = createApi({
  reducerPath: 'actionBoxCategoriesApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: ['ActionBoxCategories', 'ActionBoxCategoryInfo'],
  endpoints: (builder) => ({
    getActionBoxCategories: builder.query({
      query: (queryParams: CommonSearchParams) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_CATEGORIES_REST_URL}`,
          queryParams
        }),
      providesTags: ['ActionBoxCategories']
    }),
    updateCategoryUiPositions: builder.mutation({
      query: (requestParams: CategoryUpdateUiParams) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_CATEGORIES_REST_URL}/ui-positions`,
          method: HTTP_METHODS.PUT,
          body: requestParams
        }),
      invalidatesTags: ['ActionBoxCategories']
    }),
    getActionBoxCategoryInfo: builder.query({
      query: (requestParams: GetActionBoxCategoryInfoParams) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_CATEGORIES_REST_URL}/${requestParams.ActionBoxCategoryID}`,
          queryParams: { TenantID: requestParams.TenantID }
        }),
      providesTags: ['ActionBoxCategoryInfo'],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data) {
            dispatch(setActionBoxCategoryInfo(data))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    softDeleteActionBoxCategory: builder.mutation({
      query: (requestParams: { ActionBoxCategoryID: number }) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_CATEGORIES_REST_URL}/${requestParams.ActionBoxCategoryID}`,
          method: HTTP_METHODS.DELETE
        }),
      invalidatesTags: ['ActionBoxCategories']
    }),
    validateActionBoxCategoryFormFields: builder.mutation({
      query: (requestParams: ActionBoxCategoryFormFields) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_CATEGORIES_REST_URL}/validate`,
          method: HTTP_METHODS.POST,
          body: requestParams
        })
    }),
    createNewActionBoxCategory: builder.mutation({
      query: (requestParams: ActionBoxCategoryFormFields) =>
        generateRESTQuery({
          path: apiConstants.ACTION_BOXES_CATEGORIES_REST_URL,
          method: HTTP_METHODS.POST,
          body: requestParams
        }),
      invalidatesTags: ['ActionBoxCategories', 'ActionBoxCategoryInfo']
    }),
    editActionBoxCategory: builder.mutation({
      query: (requestParams: ActionBoxCategoryFormFields) =>
        generateRESTQuery({
          path: apiConstants.ACTION_BOXES_CATEGORIES_REST_URL,
          method: HTTP_METHODS.PUT,
          body: requestParams
        }),
      invalidatesTags: ['ActionBoxCategories', 'ActionBoxCategoryInfo']
    })
  })
})

export const {
  useGetActionBoxCategoriesQuery,
  useUpdateCategoryUiPositionsMutation,
  useSoftDeleteActionBoxCategoryMutation,
  useGetActionBoxCategoryInfoQuery,
  useCreateNewActionBoxCategoryMutation,
  useEditActionBoxCategoryMutation,
  useValidateActionBoxCategoryFormFieldsMutation
} = actionBoxCategoriesApi
